import { LineupEntry } from "@aura-frontend/omnibus-az-client";
import { ColumnDef } from "@tanstack/react-table";
import { Button, Icons } from "@win-reality/react-ui";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { capitalize } from "@/utils/capitalize";
import { splitName } from "@/utils/splitName";

import ChangeJerseyNumberModal from "./ChangeJerseyNumberModal";

function JerseyNumberCell({
  row,
  rosterId,
}: {
  row: LineupEntry;
  rosterId: string;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const rowRecord = row as unknown as Record<string, unknown>;
  const jerseyNumber =
    (rowRecord.jerseyNumber as string | number | undefined) ||
    (rowRecord.jersey_number as string | number | undefined);
  const playerId =
    (rowRecord.externalId as string | undefined) ||
    (rowRecord.external_id as string | undefined) ||
    "";

  return (
    <>
      <div className="flex items-center gap-1">
        <span className="min-w-8 text-center">{jerseyNumber ?? "-"}</span>
        <Button
          size="icon"
          variant="ghost"
          onClick={() => setIsModalOpen(true)}
          className="h-5 w-5 p-0.5 text-blue hover:bg-blue/10"
        >
          <Icons.PencilOutline className="h-4 w-4" />
        </Button>
      </div>
      <ChangeJerseyNumberModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        rosterId={rosterId}
        playerId={playerId}
      />
    </>
  );
}

export const useRosterPlayerColumns = ({ rosterId }: { rosterId: string }) => {
  const rosterPlayerColumns = useMemo((): ColumnDef<LineupEntry>[] => {
    const columns: ColumnDef<LineupEntry>[] = [
      {
        accessorKey: "jerseyNumber",
        header: "JERSEY #",
        size: 100,
        cell: ({ row: { original: row } }) => (
          <JerseyNumberCell row={row} rosterId={rosterId} />
        ),
      },
      {
        header: "First Name",
        id: "firstName",
        size: 125,
        cell: ({ row: { original: row } }) => {
          const rowRecord = row as unknown as Record<string, unknown>;
          const playerName =
            (rowRecord.playerName as string | undefined) ||
            (rowRecord.player_name as string | undefined) ||
            "";
          const { firstName } = splitName(playerName);
          const playerId =
            (rowRecord.externalId as string | undefined) ||
            (rowRecord.external_id as string | undefined);

          return playerId ? (
            <Link
              to={`/players/${playerId}/home`}
              className="text-blue hover:underline"
            >
              {firstName}
            </Link>
          ) : (
            firstName
          );
        },
      },
      {
        header: "Last Name",
        size: 125,
        id: "lastName",
        cell: ({ row: { original: row } }) => {
          const rowRecord = row as unknown as Record<string, unknown>;
          const playerName =
            (rowRecord.playerName as string | undefined) ||
            (rowRecord.player_name as string | undefined) ||
            "";
          const { lastName } = splitName(playerName);
          return lastName;
        },
      },
      {
        accessorKey: "pitchSide",
        header: "Throws",
        size: 100,
        cell: ({ row: { original: row } }) => {
          const rowRecord = row as unknown as Record<string, unknown>;
          const pitchSide =
            (rowRecord.pitchSide as string | undefined) ||
            (rowRecord.pitch_side as string | undefined);
          return pitchSide ? capitalize(pitchSide) : "";
        },
      },
      {
        accessorKey: "hitSide",
        header: "Bats",
        cell: ({ row: { original: row } }) => {
          const rowRecord = row as unknown as Record<string, unknown>;
          const hitSide =
            (rowRecord.hitSide as string | undefined) ||
            (rowRecord.hit_side as string | undefined);
          return hitSide ? capitalize(hitSide) : "";
        },
      },
    ];

    return columns;
  }, [rosterId]);

  return { rosterPlayerColumns };
};
